
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCostEstimator } from '@/context/CostEstimatorContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckIcon, RocketIcon, LaptopIcon, BuildingIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

const AppTypeOption: React.FC<{
  value: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}> = ({ value, label, description, icon, selected, onClick }) => {
  const { COSTS } = useCostEstimator();
  const cost = COSTS.appType[value as keyof typeof COSTS.appType];
  
  return (
    <div 
      className={cn(
        "border rounded-lg p-6 cursor-pointer transition-all duration-300 hover:shadow-md",
        selected 
          ? "border-primary bg-primary/10" 
          : "border-border hover:border-primary/50"
      )}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-2xl text-primary">{icon}</div>
        {selected && <CheckIcon className="h-5 w-5 text-primary" />}
      </div>
      <h3 className="font-medium text-lg">{label}</h3>
      <p className="text-sm text-muted-foreground mt-1">{description}</p>
      <p className="text-sm font-medium text-primary mt-2">
        ${cost.toLocaleString()}
      </p>
    </div>
  );
};

const AppTypeStep: React.FC = () => {
  const { t } = useTranslation();
  const { setAppType, options, goToNextStep, goToPreviousStep } = useCostEstimator();

  const handleSetAppType = (appType: any) => {
    setAppType(appType);
  };

  const isNextDisabled = !options.appType;

  return (
    <Card className="form-card">
      <CardHeader>
        <CardTitle>{t('apptype_title')}</CardTitle>
        <CardDescription>{t('apptype_subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <AppTypeOption
            value="MVP"
            label={t('apptype_mvp')}
            description={t('apptype_mvp_desc')}
            icon={<RocketIcon />}
            selected={options.appType === 'MVP'}
            onClick={() => handleSetAppType('MVP')}
          />
          <AppTypeOption
            value="Full"
            label={t('apptype_full')}
            description={t('apptype_full_desc')}
            icon={<LaptopIcon />}
            selected={options.appType === 'Full'}
            onClick={() => handleSetAppType('Full')}
          />
          <AppTypeOption
            value="Enterprise"
            label={t('apptype_enterprise')}
            description={t('apptype_enterprise_desc')}
            icon={<BuildingIcon />}
            selected={options.appType === 'Enterprise'}
            onClick={() => handleSetAppType('Enterprise')}
          />
        </div>
        <div className="flex justify-between">
          <Button variant="outline" onClick={goToPreviousStep}>
            {t('btn_previous')}
          </Button>
          <Button onClick={goToNextStep} disabled={isNextDisabled}>
            {t('btn_next')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AppTypeStep;
